import { useState, useCallback } from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "wouter";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { useIsMobile } from "@/lib/use-media";

interface ToastData {
  id: string;
  message: string;
  variant: "success" | "error" | "notice";
  /** #3169 — optional followable link, `notice` only today. Undefined
   *  renders no link at all (backwards-compatible with every existing
   *  `toast(...)`/`toast.success(...)`/`toast.error(...)`/`toast.notice(...)`
   *  call, none of which pass one). */
  href?: string;
  linkLabel?: string;
}

/** #3169 — optional second arg to `toast.notice`. A caller with no href
 *  renders exactly the plain-message toast this primitive always has. */
export interface ToastNoticeOptions {
  readonly href?: string;
  readonly linkLabel?: string;
}

let addToastFn: ((t: Omit<ToastData, "id">) => void) | null = null;

export function toast(message: string) {
  addToastFn?.({ message, variant: "success" });
}
toast.success = (message: string) => addToastFn?.({ message, variant: "success" });
toast.error = (message: string) => addToastFn?.({ message, variant: "error" });
// A server-owned disclosure that is neither a user error (amber) nor a
// success (emerald is reserved, CLAUDE.md Design Constraints) — e.g. the
// ladder-downgrade notice. Same neutral elevated styling as `success`, same
// Infinity duration as `error`: a security disclosure must not vanish in 4s.
// Ruling Q14: a transient `success` raised while a `notice`/`error` is
// showing is DROPPED, never displayed — the persistent disclosure keeps the
// slot. Because that makes "until dismissed" load-bearing, `notice` (and
// `error`) also carry an explicit Close control below — precedence without a
// way to dismiss would strand every later success toast behind an
// un-dismissable notice. Residual: a success toast raised while a disclosure
// is showing is silently not shown (not queued, not merged) until the
// disclosure is dismissed or replaced by a later notice/error.
toast.notice = (message: string, options?: ToastNoticeOptions) =>
  addToastFn?.({ message, variant: "notice", href: options?.href, linkLabel: options?.linkLabel });

/** Variants that stay until dismissed (Infinity duration, Close control, and
 *  precedence over a transient success — ruling Q14). */
const isPersistent = (variant: ToastData["variant"]): boolean =>
  variant === "error" || variant === "notice";

export function Toaster() {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const isMobile = useIsMobile();

  addToastFn = useCallback((t: Omit<ToastData, "id">) => {
    setToasts((prev) => {
      const current = prev[0];
      // Ruling Q14 precedence: a transient success never evicts a live
      // persistent disclosure (notice/error) — it is dropped outright.
      // notice/error may replace anything, including each other.
      if (current && isPersistent(current.variant) && t.variant === "success") {
        return prev;
      }
      const id = crypto.randomUUID();
      return [{ ...t, id }]; // Only one visible at a time per spec
    });
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastPrimitive.Provider duration={4000}>
      <AnimatePresence>
        {toasts.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            asChild
            open
            onOpenChange={(open) => { if (!open) dismiss(t.id); }}
            duration={isPersistent(t.variant) ? Infinity : 4000}
          >
            <motion.div
              data-testid={`toast-panel-${t.variant}`}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20, transition: { type: "spring", stiffness: 450, damping: 28 } }}
              className={cn(
                "px-4 py-3 rounded-[var(--radius-md)] flex items-start gap-3",
                "shadow-[shadow:var(--elevation-2)] text-[length:var(--text-body-size)]",
                // Colour semantics (CLAUDE.md): red reserved for Forensic Pause;
                // errors use amber, success and notice share the neutral
                // elevated styling — a notice is neither a user error nor a
                // success, so it takes success's classes, not error's.
                t.variant === "error"
                  ? "bg-bg-elevated border border-amber-500/40 text-amber-400"
                  : "bg-bg-elevated border border-border-default text-text-primary",
              )}
            >
              <ToastPrimitive.Description className="flex-1">
                {t.message}
                {/* #3169 — the notice's optional followable link. Fail-closed
                    at the caller (listener validates same-origin + exact
                    route before ever passing `href`) AND, defence in depth
                    (Round-1 review Nit 7), here too: a non-string href or
                    one carrying a `:` (an absolute/`javascript:`-style URL)
                    is never rendered even if a future caller forgets to
                    sanitize. `ToastPrimitive.Action` (Round-1 review MAJOR
                    4), not a bare `Link`, so clicking through also DISMISSES
                    this toast — otherwise a followed link leaves the
                    persistent notice toast stuck, silently dropping every
                    later `toast.success` app-wide (Ruling Q14 precedence)
                    until someone finds the separate Close control. */}
                {typeof t.href === "string" && !t.href.includes(":") && (
                  <ToastPrimitive.Action asChild altText={t.linkLabel ?? "See more"}>
                    <Link
                      href={t.href}
                      className="block mt-1 text-interactive underline underline-offset-2"
                    >
                      {t.linkLabel ?? "See more"}
                    </Link>
                  </ToastPrimitive.Action>
                )}
              </ToastPrimitive.Description>
              {isPersistent(t.variant) && (
                // Explicit dismiss for the persistent variants (Infinity duration,
                // Ruling Q14) — precedence keeps a notice/error from being silently
                // evicted by a later success, so without this control it would be
                // un-dismissable until replaced by another notice/error.
                <ToastPrimitive.Close aria-label="Dismiss" className="shrink-0 opacity-70 hover:opacity-100">
                  <X className="w-4 h-4" aria-hidden="true" />
                </ToastPrimitive.Close>
              )}
            </motion.div>
          </ToastPrimitive.Root>
        ))}
      </AnimatePresence>
      <ToastPrimitive.Viewport
        aria-live="polite"
        className={cn(
          "fixed z-50 flex flex-col gap-2",
          isMobile
            ? "top-[calc(8px+env(safe-area-inset-top))] left-1/2 -translate-x-1/2 w-[calc(100%-32px)]"
            : "top-4 right-4 w-80",
        )}
      />
    </ToastPrimitive.Provider>
  );
}
